import { useAuthenticated } from '@jotta/auth-client/useAuthStatus'
import { useFeatures } from '@jotta/grpc-connect-client/config'
import { useGetCustomer } from '@jotta/grpc-connect-client/customer'
import { TransportProvider } from '@connectrpc/connect-query'
import { addBreadcrumb } from '@sentry/react'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { Outlet, useMatches, useNavigate, useParams } from 'react-router-dom'
import {
  useGetOnboarding,
  useSetOnboarding,
} from '@jotta/grpc-js-client/onboarding'
import {
  getDebugPeople,
  PeopleDebug,
} from '../../people/internal/components/PeopleDebug/PeopleDebug'
import { usePhotoStore } from '../../store/PhotoContext'
import { waitlistStore } from '../../store/WaitlistStore'
import { TimelineActivation } from '../PhotoGallery/TimelineActivation'
import {
  PhotoSearchOverlay,
  PhotoSearchPlaceholder,
} from '../PhotoSearch/PhotoSearchOverlay'
import {
  UploadPhotosButton,
  UploadPhotosStatus,
} from '../UploadPhotos/UploadPhotos'
import { PhotoSearchResult } from '../../search/internal/PhotoSearchResult'
import { AppLayoutPortal } from '@jotta/ui/AppLayoutPortal'
import { PhotoSearchFormSimple } from '../../search/internal/PhotoSearchFormSimple'
import { authTransport } from '@jotta/grpc-connect-client/transport'
import { PhotoState } from '@jotta/grpc-connect-openapi/esm/openapi/customer/customer.v2_pb'
import { BrandIcon } from '@jotta/ui/BrandIcon'
import SvgPhotos from '@jotta/ui/SvgPhotos'
import { Trans } from '@lingui/macro'
import SvgAlbum from '@jotta/ui/SvgAlbum'
import SvgEyeSlash from '@jotta/ui/SvgEyeSlash'
import SvgSharedFolder from '@jotta/ui/SvgSharedFolder'
import { useMobileBreakpoint } from '@jotta/ui/useBreakpoint'
import { PhotoRouteId } from '../../routes/photoRouteIds'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'
import { isEnumValue } from '@jotta/utils/enum'

const getHeaderInfo = (currentRouteId: PhotoRouteId) => {
  switch (currentRouteId) {
    case PhotoRouteId.PhotosRoot:
    case PhotoRouteId.PhotosTimeline:
      return { title: <Trans>Photos</Trans>, icon: SvgPhotos }
    case PhotoRouteId.PhotosAlbum:
    case PhotoRouteId.PhotosAlbumList:
    case PhotoRouteId.PhotosAlbumsNormal:
    case PhotoRouteId.PhotosPublicAlbumInfoLoader:
    case PhotoRouteId.PhotosAlbumsLocation:
      return { title: <Trans>Collections</Trans>, icon: SvgAlbum }
    case PhotoRouteId.PhotosAllHidden:
    case PhotoRouteId.PhotosHiddenMedia:
      return { title: <Trans>Hidden</Trans>, icon: SvgEyeSlash }
    case PhotoRouteId.PhotosAlbumsShared:
    case PhotoRouteId.PhotosAllShared:
      return { title: <Trans>Shared Photos</Trans>, icon: SvgSharedFolder }
    case PhotoRouteId.PhotosShareActionFollowAlbum:
    case PhotoRouteId.PhotosShare:
    case PhotoRouteId.PhotosShareMediaId:
    case PhotoRouteId.PhotosMedia:
    case PhotoRouteId.PhotosShareActionAddPhotos:
    case PhotoRouteId.PhotosAlbumInfoLoader:
      return undefined
    default:
      return exhaustiveGuard(currentRouteId)
  }
}

const PhotosHeader = () => {
  const matches = useMatches()
  const currentRouteIdString = matches[matches.length - 1]?.id
  const headerInfo = isEnumValue(PhotoRouteId, currentRouteIdString)
    ? getHeaderInfo(currentRouteIdString)
    : undefined

  if (!headerInfo) {
    return null
  }
  return (
    <AppLayoutPortal slot="moduleNav">
      <div className="w-full bg-primary">
        <div className="flex w-full items-center justify-between border-b border-light">
          <div className="mx-4 flex h-[56px] items-center gap-2 text-lg font-medium text-primary">
            <BrandIcon className="size-5" icon={headerInfo.icon} />
            {headerInfo.title}
          </div>
          <div className="hidden xl:block">
            <UploadPhotosButton btnType="OUTLINE" />
          </div>
        </div>
      </div>
    </AppLayoutPortal>
  )
}

const PhotosFooter = () => {
  const isMobile = useMobileBreakpoint()
  return (
    <>
      <AppLayoutPortal slot="actionButton">
        {isMobile && <UploadPhotosButton btnType="FAB" />}
      </AppLayoutPortal>
      <AppLayoutPortal slot="uploadStatus">
        <UploadPhotosStatus />
      </AppLayoutPortal>
    </>
  )
}

/**
 * Root route container for all photo routes
 */
export const PhotoLayout = observer<{
  isPublic?: boolean
}>(function PhotoLayout({ isPublic = false }) {
  const features = useFeatures()
  const authenticated = useAuthenticated() === 'TRUE'
  const customer = useGetCustomer().data
  const isPhotosActivated =
    isPublic || (customer && customer.photoState !== PhotoState.NOT_ACTIVE)

  useEffect(() => {
    addBreadcrumb({
      level: 'info',
      category: 'PhotoRouteContainer',
      data: {
        section: isPhotosActivated ? 'PhotoLayout' : 'TimelineActivation',
      },
    })
  }, [isPhotosActivated])

  const navigate = useNavigate()
  const { data } = useGetOnboarding({
    enabled: authenticated,
  })
  const setOnboarding = useSetOnboarding()

  const searchActivated =
    isPhotosActivated &&
    authenticated &&
    features.photoSearch &&
    (waitlistStore.isActive || waitlistStore.isOnWaitlist)

  const peopleSearchActivated = getDebugPeople().peopleSearch && searchActivated

  const showPlaceholder = Boolean(
    isPhotosActivated &&
      authenticated &&
      features.photoSearch &&
      waitlistStore.isInactive &&
      data &&
      data.has('smartPhotoSearchBanner'),
  )

  const clickHandler = async () => {
    await setOnboarding.mutateAsync(['smartPhotoSearchBanner', false])
    navigate('/photo')
  }
  const { albumId = '' } = useParams()
  const photoStore = usePhotoStore()

  useEffect(() => {
    photoStore.currentAlbumId = albumId
  }, [albumId, photoStore])
  useEffect(() => {
    photoStore.isPublicRoute = isPublic
  }, [isPublic, photoStore])

  if (peopleSearchActivated) {
    const photoSearchStore = photoStore.search2
    return (
      <TransportProvider transport={authTransport}>
        <AppLayoutPortal slot="header">
          <PhotoSearchFormSimple />
        </AppLayoutPortal>
        {photoSearchStore.hasResults ? (
          <PhotoSearchResult search={photoSearchStore} />
        ) : (
          <Outlet />
        )}
        <PhotosFooter />
        <PeopleDebug />
      </TransportProvider>
    )
  }

  return (
    <TransportProvider transport={authTransport}>
      <PhotosHeader />
      {isPhotosActivated ? <Outlet /> : <TimelineActivation />}
      {searchActivated && <PhotoSearchOverlay />}
      {showPlaceholder && <PhotoSearchPlaceholder onClick={clickHandler} />}

      {!isPublic && <PhotosFooter />}
      <PeopleDebug />
    </TransportProvider>
  )
})
